/**
 * 용신라이트 v2.0 API 응답 데이터를 각 컴포넌트의 contentData 형식으로 변환
 *
 * 응답 구조: data.페이지.{돕는기운, 중심기운, 오행성향, 에너지타입, 대운흐름, 생활활용, 마무리}
 *          + data.우측패널
 *
 * @param {Ref<Object|null>} apiData - fetchBaseServiceAPI 응답의 data 필드
 * @returns {{ content02Data, content03Data, content04Data, content05Data, content06Data, quickUsageData }}
 */
export function useYongsinLightData(apiData) {
  const ELEMENT_INDEX = { '목': 0, '화': 1, '토': 2, '금': 3, '수': 4 };

  const ELEMENT_LIGHT_BG = {
    '목': '#edf7e4',
    '화': '#fde8e8',
    '토': '#fff6ed',
    '금': '#f0f2f5',
    '수': '#e8ecf0',
  };

  const ELEMENT_COLOR_MAP = {
    '목': '#5e9f1c',
    '화': '#c23535',
    '토': '#db9823',
    '금': '#949494',
    '수': '#404040',
  };

  const ELEMENT_SWATCH_COLORS = {
    '목': ['#5e9f1c', '#8dc95c'],
    '화': ['#d05a32', '#e8956e'],
    '토': ['#b08c2e', '#d4b96a'],
    '금': ['#8a8a8a', '#b8b8b8'],
    '수': ['#1a1a2e', '#3a5a8c'],
  };

  const ELEMENT_THEME_COLOR = {
    '목': '#5e9f1c',
    '화': '#c23535',
    '토': '#db9823',
    '금': '#949494',
    '수': '#404040',
  };

  const CARD_BORDER_COLORS = {
    color: '#f8b750',
    food: '#9aa2e6',
    action: '#96c362',
    direction: '#70bfff',
    number: '#47456d',
    time: '#f4665c',
    space: '#70bfff',
    relation: '#ffc7a3',
  };

  const splitNewline = (text) => (text ? text.split('\n') : []);
  const splitPipe = (text) => (text ? text.split('|').map((s) => s.trim()).filter(Boolean) : []);
  const removeHash = (tag) => (tag ? tag.replace(/^#/, '') : '');

  const extractQuotedText = (headline) => {
    if (!headline) return '';
    const match = headline.match(/[''‘’]([^''‘’]+)[''‘’]/);
    return match ? match[1] : '';
  };

  // "~수록" 뒤 전체를 bold
  const boldAfterSurok = (text) => {
    if (!text) return '';
    const idx = text.indexOf('수록');
    if (idx === -1) return text;
    return `${text.slice(0, idx + 2)}<span class="bold">${text.slice(idx + 2)}</span>`;
  };

  // "~은/는" 뒤 전체를 bold (희신 보조설명 첫 줄)
  const boldAfterParticle = (text) => {
    if (!text) return '';
    const match = text.match(/^(.+?[은는]\s*)(.+)$/);
    if (!match) return text;
    return `${match[1]}<span class="bold">${match[2]}</span>`;
  };

  const boldAdviceLine = (text) => {
    if (!text) return '';
    return text.replace(
      /^(.+?)(이|가|을|를|은|는|도|에|와|과|로|으로)?$/,
      (_, core, particle) => particle
        ? `<span class="bold">${core}</span>${particle}`
        : `<span class="bold">${core}</span>`,
    );
  };

  // 비율 문자열("-75.4") → 절댓값 수치
  const parseRatio = (val) => {
    if (val == null) return 0;
    const n = typeof val === 'number' ? val : parseFloat(String(val));
    return Number.isFinite(n) ? Math.abs(n) : 0;
  };

  // 페이지 접근 헬퍼
  const pages = computed(() => apiData.value ? (apiData.value['페이지'] || {}) : {});
  const yongsinOheng = computed(() => apiData.value ? (apiData.value['용신오행'] || '') : '');
  const heesinOheng = computed(() => apiData.value ? (apiData.value['희신오행'] || '') : '');
  const meta = computed(() => apiData.value ? (apiData.value['메타'] || {}) : {});
  const rightPanel = computed(() => apiData.value ? (apiData.value['우측패널'] || {}) : {});

  // ═══════════════════════════════════════════════
  // Content02: 돕는기운
  // ═══════════════════════════════════════════════
  const content02Data = computed(() => {
    const dopneun = pages.value['돕는기운'];
    const raw = dopneun ? dopneun['내용'] : null;
    if (!raw) return null;

    const coreMsg = raw['핵심메시지'];
    const headline = coreMsg ? (coreMsg['헤드라인'] || '') : '';

    const yongsinSummary = raw['용신요약'];
    const heesinSummary = raw['희신요약'];

    // 통합설명 파싱 (용신/희신 이름 prefix 하이라이트)
    const integrationText = raw['통합설명'] || '';
    const integrationLines = splitNewline(integrationText);
    const yongsinName = yongsinSummary ? (yongsinSummary['원소표시'] || '') : '';
    const heesinName = heesinSummary ? (heesinSummary['원소표시'] || '') : '';

    const speechLines = integrationLines.map((line) => {
      if (yongsinName && line.startsWith(yongsinName)) {
        return { prefix: yongsinName, prefixType: 'yongsin', suffix: line.slice(yongsinName.length) };
      }
      if (heesinName && line.startsWith(heesinName)) {
        return { prefix: heesinName, prefixType: 'heesin', suffix: line.slice(heesinName.length) };
      }
      const heesinHanja = heesinSummary ? (heesinSummary['한자'] || '') : '';
      const heesinOhengHanja = `${heesinOheng.value}(${heesinHanja})`;
      if (heesinOhengHanja.length > 2 && line.startsWith(heesinOhengHanja)) {
        return { prefix: heesinOhengHanja, prefixType: 'heesin', suffix: line.slice(heesinOhengHanja.length) };
      }
      return { prefix: '', prefixType: '', suffix: line };
    });

    const closing = raw['마무리'];
    const closingMain = closing ? (closing['메인'] || '') : '';
    const closingMainLines = splitNewline(closingMain);

    // AI신뢰도: v2.0 신규
    const aiReliability = raw['AI신뢰도'];
    const aiReliabilityText = aiReliability
      ? (aiReliability['표시문구'] || (aiReliability['신뢰도'] ? `${aiReliability['신뢰도']}%` : ''))
      : (meta.value['용신확률'] || '');

    return {
      chipLabel: raw['뱃지'] || '',
      energyTypeTitle: extractQuotedText(headline),
      descriptions: splitNewline(coreMsg ? (coreMsg['부가설명'] || '') : '').reduce(
        (acc, line) => {
          if (line === '') {
            acc.push([]);
          } else {
            if (acc.length === 0) acc.push([]);
            acc[acc.length - 1].push(line);
          }
          return acc;
        },
        [[]],
      ),
      highlightedIndices: [
        ELEMENT_INDEX[yongsinOheng.value],
        ELEMENT_INDEX[heesinOheng.value],
      ].filter((v) => v !== undefined),
      yongsinInfo: {
        name: yongsinName,
        element: yongsinOheng.value,
        keyword: removeHash(yongsinSummary ? (yongsinSummary['태그'] || '') : ''),
      },
      heesinInfo: {
        name: heesinName,
        element: heesinOheng.value,
        keyword: removeHash(heesinSummary ? (heesinSummary['태그'] || '') : ''),
      },
      speechLines,
      speechAdvice: '',
      bottomCardIntro: splitNewline(closing ? (closing['부가'] || '') : ''),
      bottomCardQuote: closingMainLines[0] || '',
      bottomCardOutro: [
        ...closingMainLines.slice(1),
        closing ? (closing['이름메시지'] || '') : '',
      ].filter(Boolean),
      yongsinLightBg: ELEMENT_LIGHT_BG[yongsinOheng.value] || '',
      heesinLightBg: ELEMENT_LIGHT_BG[heesinOheng.value] || '',
      fiveElementChart: raw['오행차트'] || null,
      aiModelReliability: aiReliabilityText,
    };
  });

  // ═══════════════════════════════════════════════
  // Content03: 중심기운 + 오행성향
  // ═══════════════════════════════════════════════
  const content03Data = computed(() => {
    const centerPage = pages.value['중심기운'];
    const ohengPage = pages.value['오행성향'];
    const centerRaw = centerPage ? centerPage['내용'] : null;
    const ohengRaw = ohengPage ? ohengPage['내용'] : null;
    if (!centerRaw && !ohengRaw) return null;

    const yongsinData = centerRaw ? centerRaw['용신'] : null;
    const heesinData = centerRaw ? centerRaw['희신'] : null;

    // v2.0: 키워드 → 키워드태그(배열). 첫 항목을 메인 키워드로 사용
    const yongsinKeyword = yongsinData
      ? (Array.isArray(yongsinData['키워드태그'])
          ? yongsinData['키워드태그'].join(', ')
          : (yongsinData['키워드'] || ''))
      : '';

    // 용신 설명 카드 텍스트
    const yongsinDescParagraphs = yongsinData
      ? [
          [
            `${apiData.value ? (apiData.value['사용자명'] || '') : ''} 님에게 필요한 핵심은`,
            `<span class="ysl-keyword">'${yongsinKeyword}' </span>입니다.`,
          ],
          ...splitNewline(yongsinData['부연설명'] || '').map((line) => [line]),
          ...splitNewline(yongsinData['조언'] || '').map((line, i) =>
            i === 0 ? [boldAdviceLine(line)] : [line],
          ),
          [boldAfterSurok(yongsinData['마무리'] || '')],
        ].filter((p) => p.some(Boolean))
      : null;

    // 희신 설명 카드
    const heesinDescParagraphs = heesinData
      ? splitNewline(heesinData['보조설명'] || '').map((line, i) =>
          i === 0 ? [boldAfterParticle(line)] : [line],
        ).filter((p) => p.some(Boolean))
      : null;

    // 오행 카드 데이터
    const elements = ['목', '화', '토', '금', '수'];
    const ohengMap = ohengRaw ? (ohengRaw['오행'] || {}) : {};
    const elementCardsData = ohengRaw
      ? elements.map((el) => {
          const data = ohengMap[el];
          if (!data) return null;
          const descLines = splitNewline(data['설명'] || '');
          return {
            element: el,
            descBold: data['한줄'] || '',
            descSuffix: '',
            descSub: descLines.length > 1 ? descLines.slice(1).join(' ') : '',
            hashtag: data['태그'] || '',
            isYongsin: data['내용신'] || false,
            isHeesin: data['내희신'] || false,
          };
        }).filter(Boolean)
      : null;

    // 오행 비율: v2.0은 오행성향.오행.{el}.비율 사용 (이전: 사주구조.오행차트.차트)
    const fiveElementRatios = elements.map((el) => ({
      element: el,
      ratio: ohengMap[el] ? (ohengMap[el]['비율'] || 0) : 0,
    }));

    // 말풍선 안 짧은 설명: 역할 텍스트에서 "기운" 앞까지 추출
    const yongsinRole = yongsinData ? (yongsinData['역할'] || '') : '';
    const parseRoleText = (role) => {
      if (!role) return '';
      let text = role.replace(/^나에게\s*/, '');
      const giunIdx = text.indexOf('기운');
      if (giunIdx !== -1) {
        text = text.slice(0, giunIdx + 2);
      }
      return text.trim();
    };
    const yongsinShortDesc = parseRoleText(yongsinRole);

    // 두 기운의 관계: v2.0은 상생관계.설명 우선, 폴백으로 활용상세.기본정보.설명
    const relationDesc = (() => {
      const sangseng = centerRaw ? centerRaw['상생관계'] : null;
      const detail = centerRaw ? centerRaw['활용상세'] : null;
      const basic = detail ? detail['기본정보'] : null;

      const yEl = yongsinData ? (yongsinData['오행'] || '') : '';
      const hEl = heesinData ? (heesinData['오행'] || '') : '';

      let sentence = '';
      if (sangseng && sangseng['설명']) {
        sentence = sangseng['설명'];
      } else if (basic && basic['설명']) {
        const rawDesc = basic['설명'];
        const combIdx = rawDesc.indexOf('조합입니다.');
        sentence = combIdx !== -1
          ? rawDesc.slice(combIdx + '조합입니다.'.length).trim()
          : rawDesc;
      }

      let descHtml = '';
      if (sentence) {
        descHtml = sentence
          .replace(new RegExp(`(${yEl}(?:\\([^)]*\\))?)`, 'g'), `<span class="ysl-relation-yongsin">$1</span>`)
          .replace(new RegExp(`(${hEl}(?:\\([^)]*\\))?)`, 'g'), `<span class="ysl-relation-heesin">$1</span>`)
          .replace(/(상생\s*관계)/g, '<span class="ysl-relation-bold">$1</span>');
      }

      const rawTip = basic ? (basic['실용조언'] || '') : '';
      const tipLines = splitPipe(rawTip);

      if (!descHtml && tipLines.length === 0) return null;
      return { descHtml, tipLines };
    })();

    // 칠판 섹션 인용문: v2.0은 오행성향.오행패턴.인용문
    const ohengSummaryLines = (() => {
      if (!ohengRaw) return [];
      const pattern = ohengRaw['오행패턴'];
      return splitNewline(pattern ? (pattern['인용문'] || '') : '');
    })();

    // 철학배너: 첫 줄 = 타이틀, 나머지 = 본문
    const guideBanner = (() => {
      const bannerText = ohengRaw ? (ohengRaw['철학배너'] || '') : '';
      const lines = splitNewline(bannerText);
      if (!lines.length) return null;
      return {
        title: lines[0].replace(/\.$/, ''),
        lines: lines.slice(1),
      };
    })();

    // 안심멘트 (빈 문자열이면 null)
    const ansimMessage = ohengRaw ? (ohengRaw['안심멘트'] || '') : '';

    return {
      yongsinInfo: {
        name: yongsinData ? (yongsinData['원소표시'] || '') : '',
        element: yongsinOheng.value,
        keyword: yongsinKeyword,
      },
      heesinInfo: {
        name: `${heesinOheng.value}(${heesinData ? (heesinData['한자'] || '') : ''})`,
        element: heesinOheng.value,
        keyword: removeHash(heesinData ? (heesinData['태그'] || '') : ''),
      },
      yongsinDescription: yongsinShortDesc,
      yongsinDescParagraphs,
      heesinDescParagraphs,
      yongsinNature: yongsinData ? (yongsinData['상징'] || '') : '',
      heesinNature: heesinData ? (heesinData['물상'] || '') : '',
      yongsinTag: removeHash(yongsinData ? (yongsinData['태그'] || '') : ''),
      heesinTag: removeHash(heesinData ? (heesinData['태그'] || '') : ''),
      elementCardsData,
      fiveElementRatios,
      relationDesc,
      ohengSummaryLines,
      guideBanner,
      ansimMessage,
    };
  });

  // ═══════════════════════════════════════════════
  // Content04: 에너지타입 (v2.0에서 사주구조 대체)
  // ═══════════════════════════════════════════════
  const content04Data = computed(() => {
    const energyPage = pages.value['에너지타입'];
    const raw = energyPage ? energyPage['내용'] : null;
    if (!raw) return null;

    const elements = ['목', '화', '토', '금', '수'];

    // 오행 비율: 돕는기운.오행차트 우선, 폴백 오행성향.오행.{el}.비율
    const dopneunRaw = pages.value['돕는기운'] ? pages.value['돕는기운']['내용'] : null;
    const ohengRaw = pages.value['오행성향'] ? pages.value['오행성향']['내용'] : null;
    const ohengChart = dopneunRaw ? (dopneunRaw['오행차트'] || {}) : {};
    const ohengMap = ohengRaw ? (ohengRaw['오행'] || {}) : {};
    const fiveElementRatios = elements.map((el) => {
      const chartVal = ohengChart[el] ? ohengChart[el]['원본값'] : null;
      const ratio = chartVal != null ? chartVal : (ohengMap[el] ? (ohengMap[el]['비율'] || 0) : 0);
      return { element: el, ratio };
    });

    // 음양분석: 균형("내향 쪽"/"외향 쪽"), 비율("-75.4")
    const yinYang = raw['음양분석'] || {};
    const yinYangBalance = yinYang['균형'] || '';
    const yinYangAbs = parseRatio(yinYang['비율']);
    const isIntrovertDominant = yinYangBalance.includes('내향');
    const introvertRatio = Math.round(isIntrovertDominant ? yinYangAbs : (100 - yinYangAbs));
    const extrovertRatio = 100 - introvertRatio;
    const yinYangCommentLines = splitNewline(yinYang['코멘트'] || '');

    // 조후분석: 균형("차가움 강함"/"뜨거움 강함"), 비율("-73.1")
    const johu = raw['조후분석'] || {};
    const johuBalance = johu['균형'] || '';
    const johuAbs = parseRatio(johu['비율']);
    const isColdDominant = johuBalance.includes('차가움') || johuBalance.includes('차가');
    const coldRatio = Math.round(isColdDominant ? johuAbs : (100 - johuAbs));
    const hotRatio = 100 - coldRatio;
    const johuCommentLines = splitNewline(johu['코멘트'] || '');

    // 구조 요약 (칠판 인용문): 오행성향.오행패턴.인용문
    const ohengPattern = ohengRaw ? (ohengRaw['오행패턴'] || {}) : {};
    const structureSummary = splitNewline(ohengPattern['인용문'] || '');

    return {
      fiveElementRatios,
      yinYangRatio: extrovertRatio,
      structureSummary,
      energyStyleTitle: raw['타입명'] || '',
      energyStyleDesc: raw['타입설명'] || '',
      balanceLabel: yinYangBalance,
      balanceDesc: yinYang['라벨'] || '',
      introvertRatio,
      extrovertRatio,
      introvertLabel: introvertRatio >= extrovertRatio ? '강함' : '약함',
      extrovertLabel: extrovertRatio > introvertRatio ? '강함' : '약함',
      balanceSummaryTitle: yinYangCommentLines[0] || '',
      balanceSummaryLines: yinYangCommentLines.slice(1),
      johuLabel: johuBalance || '조후',
      johuDesc: johu['라벨'] || '',
      hotRatio,
      coldRatio,
      johuSummaryTitle: johuCommentLines[0] || '',
      johuSummaryLines: johuCommentLines.slice(1),
      ohengPattern: ohengPattern['패턴'] || '',
      sinGangInfo: meta.value['신강여부'] || '',
      yinYangState: meta.value['음양상태'] || '',
      johuState: meta.value['조후상태'] || '',
      // 에너지타입 v2.0 신규 필드 (강점/성장포인트/실천가이드)
      strengths: raw['강점'] || null,
      growthPoints: raw['성장포인트'] || null,
      actionGuide: raw['실천가이드'] || null,
    };
  });

  // ═══════════════════════════════════════════════
  // Content05: 생활활용 + 대운흐름(왼쪽 페이지)
  // ═══════════════════════════════════════════════
  const content05Data = computed(() => {
    const lifePage = pages.value['생활활용'];
    const raw = lifePage ? lifePage['내용'] : null;
    const daeunPage = pages.value['대운흐름'];
    const daeunRaw = daeunPage ? daeunPage['내용'] : null;
    if (!raw && !daeunRaw) return null;

    const yEl = yongsinOheng.value;
    const hEl = heesinOheng.value;

    // 용신/희신 라벨 태그 파싱: [{라벨, 오행}] → {yongsin, heesin}
    const parseYHTags = (rawTags) => {
      const result = { yongsin: '', heesin: '' };
      if (!Array.isArray(rawTags)) return result;
      rawTags.forEach((t) => {
        if (!t || typeof t !== 'object') return;
        const label = String(t['라벨'] || '').replace(/^#/, '').trim();
        const el = t['오행'];
        if (!label) return;
        if (el === yEl) result.yongsin = label;
        else if (el === hEl) result.heesin = label;
        else if (!result.yongsin) result.yongsin = label;
        else if (!result.heesin) result.heesin = label;
      });
      return result;
    };

    // 생활활용 섹션들 (v2.0 필드명: 활동, 공간꾸미기)
    const colorSec = raw ? (raw['색상'] || {}) : {};
    const food = raw ? (raw['음식'] || {}) : {};
    const action = raw ? (raw['활동'] || {}) : {};
    const direction = raw ? (raw['방향'] || {}) : {};
    const number = raw ? (raw['숫자'] || {}) : {};
    const time = raw ? (raw['시간'] || {}) : {};
    const space = raw ? (raw['공간꾸미기'] || {}) : {};
    const relation = raw ? (raw['관계'] || {}) : {};

    const yColor = ELEMENT_THEME_COLOR[yEl] || '#333';
    const hColor = ELEMENT_THEME_COLOR[hEl] || '#333';

    // ── 대운흐름 (왼쪽 페이지) ──
    const daeun = buildDaeunData(daeunRaw);

    return {
      // 왼쪽 페이지: 대운흐름
      daeun,

      // 공통 오른쪽 페이지 헤더
      tipMessage: raw ? (raw['소개'] || '') : '',
      yongsinColor: yColor,
      heesinColor: hColor,
      yongsinOheng: yEl,
      heesinOheng: hEl,

      // 카드1: 색상
      colorCard: {
        borderColor: CARD_BORDER_COLORS.color,
        yongsinSwatches: ELEMENT_SWATCH_COLORS[yEl] || ['#999', '#bbb'],
        heesinSwatches: ELEMENT_SWATCH_COLORS[hEl] || ['#999', '#bbb'],
        yongsinColors: colorSec['용신색'] || '',
        heesinColors: colorSec['희신색'] || '',
        lines: splitNewline(colorSec['설명'] || ''),
        tags: parseYHTags(colorSec['태그']),
      },

      // 카드2: 음식
      foodCard: {
        borderColor: CARD_BORDER_COLORS.food,
        title: food['타이틀'] || '',
        lines: splitNewline(food['내용'] || ''),
        tags: (() => {
          const rawTags = food['태그'];
          if (!rawTags) return [];
          const list = Array.isArray(rawTags) ? rawTags : [rawTags];
          return list
            .flatMap((t) => String(t).split(/\s+/))
            .map((t) => t.replace(/^#/, '').trim())
            .filter(Boolean);
        })(),
      },

      // 카드3: 활동 (v2.0: 행동 → 활동)
      actionCard: {
        borderColor: CARD_BORDER_COLORS.action,
        title: action['타이틀'] || '',
        lines: splitNewline(action['내용'] || ''),
        tags: parseYHTags(action['태그']),
      },

      // 카드4: 방향
      directionCard: {
        borderColor: CARD_BORDER_COLORS.direction,
        yongsinDirection: direction['용신방향'] || '',
        heesinDirection: direction['희신방향'] || '',
        bullets: Array.isArray(direction['일상예시']) ? direction['일상예시'] : [],
        note: direction['안내'] || '',
      },

      // 카드5: 숫자
      numberCard: {
        borderColor: CARD_BORDER_COLORS.number,
        yongsinNumbers: number['용신숫자']
          ? String(number['용신숫자']).split(/[,，]\s*/).map((s) => s.trim()).filter(Boolean)
          : [],
        heesinNumbers: number['희신숫자']
          ? String(number['희신숫자']).split(/[,，]\s*/).map((s) => s.trim()).filter(Boolean)
          : [],
        lines: splitNewline(number['설명'] || ''),
        tags: parseYHTags(number['태그']),
      },

      // 카드6: 시간
      timeCard: {
        borderColor: CARD_BORDER_COLORS.time,
        yongsinTime: time['용신시간'] || '',
        heesinTime: time['희신시간'] || '',
        lines: [time['용신설명'] || '', time['희신설명'] || ''].filter(Boolean),
      },

      // 카드7: 공간꾸미기 (v2.0: 공간 → 공간꾸미기)
      spaceCard: {
        borderColor: CARD_BORDER_COLORS.space,
        lines: splitNewline(space['내용'] || ''),
        tags: parseYHTags(space['태그']),
      },

      // 카드8: 관계
      relationCard: {
        borderColor: CARD_BORDER_COLORS.relation,
        title: relation['타이틀'] || '',
        lines: splitNewline(relation['내용'] || ''),
        tags: parseYHTags(relation['태그']),
      },
    };
  });

  // 대운흐름 데이터 가공
  const buildDaeunData = (daeunRaw) => {
    if (!daeunRaw) return null;

    const allItems = Array.isArray(daeunRaw['대운상세']) ? daeunRaw['대운상세'] : [];
    const timelineAll = allItems.map((item) => ({
      name: item['대운'] || '',
      age: `${item['나이']}세`,
      current: !!item['현재대운'],
    }));

    const currentIdx = timelineAll.findIndex((d) => d.current);
    const timeline = currentIdx >= 0
      ? timelineAll.slice(Math.max(0, currentIdx - 2), currentIdx + 3)
      : timelineAll.slice(0, 5);

    const currentItem = currentIdx >= 0 ? allItems[currentIdx] : null;

    // 희기칩 → 뱃지
    const chips = currentItem && Array.isArray(currentItem['희기칩']) ? currentItem['희기칩'] : [];
    const keywords = chips.map((chip) => {
      const isRed = /용신|희신/.test(chip);
      return {
        label: chip.replace(/—/g, '-'),
        type: isRed ? 'red' : 'gray',
      };
    });

    return {
      timeline,
      currentName: currentItem ? (currentItem['대운'] || '') : '',
      currentAge: currentItem ? `${currentItem['나이']}세` : '',
      title: currentItem ? (currentItem['십성부제'] || '') : '',
      flowLines: currentItem ? splitNewline(currentItem['운세분석'] || '') : [],
      keywords,
      closingBanner: splitNewline(daeunRaw['철학배너'] || ''),
    };
  };

  // ═══════════════════════════════════════════════
  // Content06: 마무리
  // ═══════════════════════════════════════════════
  const content06Data = computed(() => {
    const closingPage = pages.value['마무리'];
    const raw = closingPage ? closingPage['내용'] : null;
    const dopneunRaw = pages.value['돕는기운'] ? pages.value['돕는기운']['내용'] : null;

    // 요약 라인: 캡처카드.타입한줄 우선, 폴백으로 돕는기운.핵심메시지.헤드라인
    const capture = raw ? (raw['캡처카드'] || {}) : {};
    let energySummaryLines = [];
    if (capture['타입한줄']) {
      energySummaryLines = [capture['타입한줄']
        .replace(/['''']/g, '')
        .replace(/\s*입니다\.\s*$/, '')
        .trim()];
    } else if (dopneunRaw) {
      const coreMsg = dopneunRaw['핵심메시지'];
      const headline = coreMsg ? (coreMsg['헤드라인'] || '') : '';
      energySummaryLines = splitNewline(headline)
        .slice(1)
        .map((l) => l.replace(/['''']/g, '').replace(/\s*입니다\.\s*$/, '').trim())
        .filter(Boolean);
    }

    // 용신/희신 이름: 캡처카드 우선
    let yongsinName = capture['원소표시'] || '';
    let heesinName = capture['희신표시'] || '';
    if ((!yongsinName || !heesinName) && dopneunRaw) {
      const ys = dopneunRaw['용신요약'];
      const hs = dopneunRaw['희신요약'];
      if (!yongsinName && ys) yongsinName = ys['원소표시'] || '';
      if (!heesinName && hs) heesinName = hs['원소표시'] || '';
    }

    const greeting = raw ? (raw['인사'] || {}) : {};

    return {
      messageLines1: splitNewline(greeting['메인'] || ''),
      messageLines2: splitNewline(greeting['부가'] || ''),
      highlightMessage: raw ? (raw['격려문구'] || '') : '',
      energySummaryLines,
      yongsinInfo: {
        name: yongsinName,
        element: yongsinOheng.value,
      },
      heesinInfo: {
        name: heesinName,
        element: heesinOheng.value,
      },
    };
  });

  // ═══════════════════════════════════════════════
  // QuickUsage: DetailPanel 우측패널
  // ═══════════════════════════════════════════════
  const quickUsageData = computed(() => {
    const panel = rightPanel.value;
    if (!panel || (!panel['용신카드'] && !panel['희신카드'])) return null;

    const yCard = panel['용신카드'] || null;
    const hCard = panel['희신카드'] || null;
    const usage = panel['활용요약'] || {};

    const yEl = yongsinOheng.value;
    const hEl = heesinOheng.value;

    const keywordOf = (card) => {
      if (!card) return '';
      const tags = card['키워드태그'];
      if (Array.isArray(tags) && tags.length) return tags.join(', ');
      return removeHash(card['태그'] || '');
    };

    const pickMain = (sec) => (sec && sec['용신']) || '';
    const pickSub = (sec) => (sec && sec['희신']) || '';

    return {
      yongsin: yCard ? {
        label: yCard['라벨'] || '중심기운 · 용신',
        element: yEl,
        name: yCard['원소표시'] || '',
        description: yCard['설명'] || '',
        keyword: keywordOf(yCard),
        color: ELEMENT_COLOR_MAP[yEl] || '#333',
      } : null,
      heesin: hCard ? {
        label: hCard['라벨'] || '보조기운 · 희신',
        element: hEl,
        name: hCard['원소표시'] || '',
        description: hCard['설명'] || '',
        keyword: keywordOf(hCard),
        color: ELEMENT_COLOR_MAP[hEl] || '#333',
      } : null,
      grid: [
        { theme: 'color', label: '색상', main: pickMain(usage['색상']), sub: pickSub(usage['색상']) },
        { theme: 'direction', label: '방향', main: pickMain(usage['방향']), sub: pickSub(usage['방향']) },
        { theme: 'number', label: '숫자', main: pickMain(usage['숫자']), sub: pickSub(usage['숫자']) },
        { theme: 'time', label: '시간', main: pickMain(usage['시간']), sub: pickSub(usage['시간']) },
      ],
    };
  });

  return {
    content02Data,
    content03Data,
    content04Data,
    content05Data,
    content06Data,
    quickUsageData,
  };
}
